<script>
/* global axios */
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'LinkStaffModal',

  components: {
    BaseModal,
  },

  props: {
    staffs: {
      type: Array,
      default: () => [],
    },
    scheduleId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isConfirming: false,
      isAllLoading: false,
      selectedStaffOptions: [],
    };
  },

  computed: {
    apiUrl() {
      return `/api/v1/accounts/${this.accountId}/proxy/knowledge`;
    },
    accountId() {
      return this.$route.params.accountId;
    },
    branchId() {
      return this.$route.params.branchId;
    },
    branchStaffList() {
      return this.$store.state.staffV2?.staffList || [];
    },
    assignedStaffIds() {
      return this.staffs.map(s => s.id || s.staff.id);
    },
    availableStaffOptions() {
      return this.branchStaffList
        .filter(staff => !this.assignedStaffIds.includes(staff.id || staff.staff.id))
        .map(staff => ({
          value: staff.id || staff.staff.id,
          label: `${staff.name || staff.staff.name}`,
        }));
    },
  },

  methods: {
    async fetchStaff() {
      try {
        await this.$store.dispatch('staffV2/updateFiltersAndFetch', { branch_id: this.branchId });
      } catch (err) {
        console.error(err);
        useAlert(`Failed to fetch staff list`);
      }
    },

    async assignStaff(staffId) {
      const payload = { schedule_id: this.scheduleId };
      const response = await axios.post(
        `${this.apiUrl}/accounts/${this.accountId}/staff/${staffId}/schedule`,
        payload
      );
      if (response.status >= 400) {
        throw new Error(`Failed to assign staff ${staffId}`);
      }
    },

    async handleConfirm() {
      if (!this.selectedStaffOptions.length) {
        useAlert('Please select at least one staff');
        return;
      }

      this.isConfirming = true;

      try {
        const staffIds = this.selectedStaffOptions.map(o => o.value);

        for (const staffId of staffIds) {
          await this.assignStaff(staffId);
        }

        useAlert('Staff successfully linked to schedule');
        this.$store.dispatch('onboarding/completeLocalTask', 'link_staff_to_schedule');
        this.$emit('refresh');
        this.$emit('close');
      } catch (err) {
        console.error(err);
        useAlert('Some staff could not be linked. Please try again.');
      } finally {
        this.isConfirming = false;
      }
    }
  },

  async mounted() {
    this.isAllLoading = true;
    await this.fetchStaff();
    this.isAllLoading = false;
  },
};
</script>

<template>
  <BaseModal size="large" @close="$emit('close')">
    <template #title>
      Assign Staffs to Schedule
    </template>

    <!-- Staff Selection with Multiselect -->
    <div class="p-4">
      <label class="text-sm font-medium mb-2 block">Select Staffs</label>
      <multiselect
        v-model="selectedStaffOptions"
        :options="availableStaffOptions"
        label="label"
        track-by="value"
        :multiple="true"
        :searchable="true"
        :clear-on-select="false"
        placeholder="Select staffs to assign..."
        :disabled="isAllLoading"
      />
    </div>

    <template #footer>
      <div class="flex justify-end gap-3 mt-4">
        <woot-button
          color-scheme="secondary"
          @click="$emit('close')"
          :disabled="isAllLoading || isConfirming"
        >
          Cancel
        </woot-button>

        <woot-button
          color-scheme="primary"
          @click="handleConfirm"
          :disabled="isAllLoading"
          :is-loading="isConfirming"
        >
          Link
        </woot-button>
      </div>
    </template>
  </BaseModal>
</template>